import { useTranslation } from "react-i18next";
import { useStore } from "../../store/store";
import { SocketEvents } from "../hooks/useSocket/types";

/**
 * Modal shown to a single auditor (matched by fullName) when an admin
 * in livekit-client clicks "Invite" next to their name in the auditor list.
 *
 * Clicking "Join" opens the joinUrl provided by the admin (the same URL
 * the admin is using in livekit-client, stripped of admin tokens).
 *
 * Both actions broadcast an `inviteAuditorResponse` socket event so the
 * admin in livekit-client gets a notification telling them whether the
 * auditor accepted or declined.
 */
const InviteNotification = () => {
	const { t } = useTranslation();
	const invitation = useStore((state) => state.auditorInvitation);
	const setAuditorInvitation = useStore((state) => state.setAuditorInvitation);
	const socket = useStore((state) => state.socket);
	const listenerFullName = useStore((state) => state.listenerFullName);

	if (!invitation) return null;

	const sendResponse = (response: "accepted" | "declined") => {
		try {
			if (socket && socket.readyState === WebSocket.OPEN) {
				socket.send(
					JSON.stringify({
						type: SocketEvents.inviteAuditorResponse,
						data: {
							fullName: (listenerFullName || invitation.fullName || "").trim(),
							liveId: invitation.liveId,
							invitationId: invitation.invitationId,
							response,
							respondedAt: new Date().toISOString(),
						},
					}),
				);
			}
		} catch (e) {
			console.error("Failed to send invitation response", e);
		}
	};

	const handleJoin = () => {
		sendResponse("accepted");
		try {
			window.open(invitation.joinUrl, "_blank", "noopener,noreferrer");
		} catch {
			window.location.href = invitation.joinUrl;
		}
		setAuditorInvitation(null);
	};

	const handleDismiss = () => {
		sendResponse("declined");
		setAuditorInvitation(null);
	};

	const inviterLabel = invitation.invitedBy
		? t("live_page.invite_from", { name: invitation.invitedBy })
		: t("live_page.invite_from_admin");

	return (
		<div
			className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/70 p-4"
			style={{ pointerEvents: "auto" }}
		>
			<div
				className="relative z-10 w-full max-w-md rounded-lg bg-rteSecondary p-6 shadow-lg"
				onClick={(e) => e.stopPropagation()}
			>
				<h2 className="mb-2 text-lg font-semibold text-secondary">
					{t("live_page.invite_title")}
				</h2>
				<p className="mb-1 text-sm text-secondary">{inviterLabel}</p>
				<p className="mb-4 text-sm text-secondary">{t("live_page.invite_message")}</p>
				<div className="flex flex-col gap-3 sm:flex-row">
					<button
						type="button"
						onClick={handleDismiss}
						className="w-full rounded-lg border border-secondary p-3 font-medium text-secondary"
					>
						{t("live_page.invite_decline")}
					</button>
					<button
						type="button"
						onClick={handleJoin}
						className="w-full rounded-lg bg-rte p-3 font-medium text-primary"
					>
						{t("live_page.invite_join")}
					</button>
				</div>
			</div>
		</div>
	);
};

export default InviteNotification;
